/**
 * Manage, Assign: the Assign wizard (plan docs/plans/lms-plan.md 8.2 "The
 * Assign wizard", 5.4, 6.5, 18.16; plan section 13 tab 7; WP-43; R6, R8, G9).
 *
 * Replaces WP-03's placeholder in place: tabs/manage/subviews.ts imports this
 * file's DEFAULT export (a component with no props), mounted at
 * /lms/manage/assign/* (plan 18.3).
 *
 * The four steps of 8.2:
 *   1 Course      pick a published course or scenario (CoursePicker) and,
 *                 optionally, pin its current version (5.4: otherwise each
 *                 person trains on the version current when they start).
 *   2 Audience    the four selectors, combined as a union (AudienceBuilder:
 *                 sites, platform roles at this site, groups, people); the
 *                 resolved list comes from POST /admin/campaigns/preview
 *                 (useCampaignPreview) with its count and each person's
 *                 outcome, and a tick to leave someone out (exclude_user_ids).
 *   3 Details     the due window in days (default 30, 5.8 default_due_days;
 *                 1 to 365), a message, and the force switch ("also send to
 *                 people who already hold a current completion", which also
 *                 lifts a standing waiver, 18.16); force re-runs the preview.
 *   4 Review      everything in one place, then Send: POST /admin/campaigns
 *                 with the Appendix A body plus exclude_user_ids, and the
 *                 outcome table of the answer (SendResult).
 * The campaign kind follows the course (wizard.ts kindForCategory): a
 * scenario refresher, an SOP update, or a manual send.
 */
import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { ArrowLeft, ArrowRight, Loader2, Send } from 'lucide-react';
import { useAuth } from '../../../../contexts/AuthContext';
import { useToast } from '../../../../ui/Toast';
import { fieldCls, primaryBtnCls, secondaryBtnCls } from '../../../../ui/styles';
import { useLms } from '../../../LmsProvider';
import { apiErrorMessage } from '../../../api/client';
import type { AdminCourseItem } from '../../../api/assignments';
import {
  audienceIsEmpty,
  CAMPAIGN_DUE_DAYS_MAX,
  CAMPAIGN_DUE_DAYS_MIN,
  CAMPAIGN_MESSAGE_MAX,
  CAMPAIGN_OUTCOMES,
  DEFAULT_CAMPAIGN_DUE_DAYS,
  emptyAudience,
  sendCampaign,
} from '../../../api/campaigns';
import type { CampaignAudience, SendCampaignResult } from '../../../api/campaigns';
import AudienceBuilder from '../../../components/pickers/AudienceBuilder';
import AudiencePreview, { includedRecipients } from '../../../components/pickers/AudiencePreview';
import CoursePicker, { CATEGORY_LABELS } from '../../../components/pickers/CoursePicker';
import useCampaignPreview from '../../../components/pickers/useCampaignPreview';
import {
  cardCls,
  ErrorNote,
  formatDay,
  labelCls,
  metaCls,
  microLabelCls,
  OUTCOME_LABELS,
  parseWholeDays,
  peopleCount,
  sectionTitleCls,
} from '../../../components/pickers/shared';
import { CAMPAIGN_KIND_LABELS } from '../campaigns/labels';
import SendResult from './SendResult';
import { buildSendInput, describeAudience, dueDateFrom, kindForCategory, WIZARD_STEPS } from './wizard';
import type { WizardStep } from './wizard';

function Fact({ label, children, testId }: { label: string; children: ReactNode; testId?: string }) {
  return (
    <div>
      <dt className={microLabelCls}>{label}</dt>
      <dd className="mt-0.5 text-sm text-gray-900 dark:text-gray-100" data-testid={testId}>
        {children}
      </dd>
    </div>
  );
}

export function AssignView() {
  const { toast } = useToast();
  const { siteName } = useLms();
  const { availableSites } = useAuth();

  const [step, setStep] = useState<WizardStep>(0);
  const [reached, setReached] = useState<WizardStep>(0);
  const [courseId, setCourseId] = useState<string | null>(null);
  const [course, setCourse] = useState<AdminCourseItem | null>(null);
  const [pinVersion, setPinVersion] = useState(false);
  const [audience, setAudience] = useState<CampaignAudience>(emptyAudience);
  const [excluded, setExcluded] = useState<string[]>([]);
  const [dueDays, setDueDays] = useState(String(DEFAULT_CAMPAIGN_DUE_DAYS));
  const [message, setMessage] = useState('');
  const [force, setForce] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [result, setResult] = useState<SendCampaignResult | null>(null);

  const versionId = pinVersion && course?.current_version ? course.current_version.id : null;
  const { preview, loading: previewLoading, error: previewError, reload: reloadPreview } = useCampaignPreview({
    courseId,
    audience,
    courseVersionId: versionId,
    force,
  });
  const { people: included, tally } = includedRecipients(preview, excluded);
  const days = parseWholeDays(dueDays, CAMPAIGN_DUE_DAYS_MIN, CAMPAIGN_DUE_DAYS_MAX);
  const messageLength = message.trim().length;
  const messageTooLong = messageLength > CAMPAIGN_MESSAGE_MAX;

  const siteNames = useMemo(() => {
    const out: Record<string, string> = {};
    for (const s of availableSites ?? []) out[s.id] = s.name;
    return out;
  }, [availableSites]);

  const audienceReady = !audienceIsEmpty(audience) && preview !== null && !previewError && !previewLoading && included.length > 0;
  const canLeave: boolean[] = [Boolean(course), audienceReady, days !== null && !messageTooLong, false];
  const canSend = Boolean(course) && audienceReady && days !== null && !messageTooLong && !sending;

  const goTo = (target: WizardStep) => {
    if (target <= reached) setStep(target);
  };
  const next = () => {
    if (!canLeave[step] || step >= 3) return;
    const to = (step + 1) as WizardStep;
    setStep(to);
    if (to > reached) setReached(to);
  };
  const back = () => {
    if (step > 0) setStep((step - 1) as WizardStep);
  };

  const reset = () => {
    setStep(0);
    setReached(0);
    setCourseId(null);
    setCourse(null);
    setPinVersion(false);
    setAudience(emptyAudience());
    setExcluded([]);
    setDueDays(String(DEFAULT_CAMPAIGN_DUE_DAYS));
    setMessage('');
    setForce(false);
    setSendError(null);
    setResult(null);
  };

  const send = async () => {
    if (!course || days === null || !canSend) return;
    setSending(true);
    setSendError(null);
    try {
      const answer = await sendCampaign(
        buildSendInput({
          course,
          pinVersion,
          audience,
          dueDays: days,
          message,
          force,
          excluded,
          previewIds: preview ? preview.recipients.map((r) => r.user_id) : null,
        })
      );
      setResult(answer);
      toast(`Sent ${course.title}.`, { type: 'success' });
    } catch (err) {
      const text = apiErrorMessage(err, 'Could not send the course.');
      setSendError(text);
      toast(text, { type: 'error' });
    } finally {
      setSending(false);
    }
  };

  if (result && course) {
    return <SendResult result={result} courseTitle={course.title} onSendAnother={reset} />;
  }

  const kind = kindForCategory(course?.category);
  const dueOn = days !== null ? formatDay(dueDateFrom(new Date(), days)) : null;
  const tallyLine = CAMPAIGN_OUTCOMES.filter((o) => tally[o] > 0)
    .map((o) => `${OUTCOME_LABELS[o]}: ${tally[o].toLocaleString()}`)
    .join(', ');

  return (
    <div className="space-y-4" data-testid="assign-wizard">
      <ol className="flex flex-wrap items-center gap-2" aria-label="Assign steps">
        {WIZARD_STEPS.map((label, i) => {
          const current = i === step;
          const open = i <= reached;
          return (
            <li key={label}>
              <button
                type="button"
                onClick={() => goTo(i as WizardStep)}
                disabled={!open}
                aria-current={current ? 'step' : undefined}
                data-testid={`wizard-step-${i}`}
                className={`inline-flex min-h-[44px] items-center gap-2 rounded-lg px-2.5 text-sm font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed sm:min-h-0 sm:py-1.5 ${
                  current
                    ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400'
                    : open
                      ? 'text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700'
                      : 'text-gray-400 dark:text-gray-500'
                }`}
              >
                <span
                  className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${
                    current ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                  }`}
                >
                  {i + 1}
                </span>
                {label}
              </button>
            </li>
          );
        })}
      </ol>

      {step === 0 && (
        <section className={`${cardCls} space-y-4`} aria-labelledby="lms-assign-course">
          <h2 id="lms-assign-course" className={sectionTitleCls}>
            Which course or scenario?
          </h2>
          <div className="max-w-xl">
            <CoursePicker
              value={courseId}
              onChange={(id, c) => {
                setCourseId(id);
                setCourse(c);
                setPinVersion(false);
              }}
              label="Course"
            />
          </div>
          {course && course.current_version && (
            <div className="space-y-3" data-testid="assign-course-summary">
              <p className={metaCls}>
                {`${CATEGORY_LABELS[course.category] ?? course.category} \u00b7 version ${course.current_version.version_no}, published ${formatDay(course.current_version.published_at)} \u00b7 sent as: ${CAMPAIGN_KIND_LABELS[kind]}`}
              </p>
              <label className="flex min-h-[44px] cursor-pointer items-start gap-3 text-sm sm:min-h-0">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
                  checked={pinVersion}
                  onChange={(e) => setPinVersion(e.target.checked)}
                  data-testid="pin-version"
                />
                <span>
                  <span className="block font-medium text-gray-900 dark:text-gray-100">{`Pin to version ${course.current_version.version_no}`}</span>
                  <span className={metaCls}>
                    Everyone trains on this version even if a newer one is published before they start. Leave it off to use the
                    version current when each person starts.
                  </span>
                </span>
              </label>
            </div>
          )}
        </section>
      )}

      {step === 1 && (
        <section className="space-y-4" aria-labelledby="lms-assign-audience">
          <h2 id="lms-assign-audience" className={sectionTitleCls}>
            {`Who should take ${course?.title ?? 'it'}?`}
          </h2>
          <AudienceBuilder
            value={audience}
            onChange={(next) => setAudience(next)}
            siteName={siteName}
          />
          <div className={`${cardCls} space-y-2`}>
            <h3 className={sectionTitleCls}>Who this reaches</h3>
            <AudiencePreview
              preview={preview}
              loading={previewLoading}
              error={previewError}
              onRetry={reloadPreview}
              excluded={excluded}
              onExcludedChange={setExcluded}
            />
          </div>
        </section>
      )}

      {step === 2 && (
        <section className={`${cardCls} space-y-4`} aria-labelledby="lms-assign-details">
          <h2 id="lms-assign-details" className={sectionTitleCls}>
            Due date and message
          </h2>
          <div>
            <label htmlFor="lms-assign-due-days" className={labelCls}>
              Due within (days)
            </label>
            <div className="flex flex-wrap items-center gap-3">
              <input
                id="lms-assign-due-days"
                data-testid="assign-due-days"
                type="number"
                inputMode="numeric"
                min={CAMPAIGN_DUE_DAYS_MIN}
                max={CAMPAIGN_DUE_DAYS_MAX}
                step={1}
                className={`${fieldCls} min-h-[44px] w-28 sm:min-h-0`}
                value={dueDays}
                onChange={(e) => setDueDays(e.target.value)}
                aria-invalid={days === null ? true : undefined}
              />
              {dueOn && <span className={metaCls}>{`Due ${dueOn}`}</span>}
            </div>
            {days === null && (
              <p className="mt-1 text-xs text-red-600 dark:text-red-400" data-testid="assign-due-days-error">
                {`Enter a whole number of days from ${CAMPAIGN_DUE_DAYS_MIN} to ${CAMPAIGN_DUE_DAYS_MAX}.`}
              </p>
            )}
          </div>
          <div>
            <label htmlFor="lms-assign-message" className={labelCls}>
              Message <span className="font-normal text-gray-500 dark:text-gray-400">(optional; shown in the notice and on the assignment)</span>
            </label>
            <textarea
              id="lms-assign-message"
              data-testid="assign-message"
              className={`${fieldCls} w-full`}
              rows={3}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
            <p className={`mt-1 text-xs ${messageTooLong ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>
              {`${messageLength.toLocaleString()} of ${CAMPAIGN_MESSAGE_MAX.toLocaleString()} characters`}
            </p>
          </div>
          <label className="flex min-h-[44px] cursor-pointer items-start gap-3 text-sm sm:min-h-0">
            <input
              type="checkbox"
              role="switch"
              aria-checked={force}
              className="peer sr-only"
              checked={force}
              onChange={(e) => setForce(e.target.checked)}
              data-testid="force-switch"
            />
            <span
              aria-hidden="true"
              className={`relative mt-0.5 inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors duration-150 peer-focus-visible:ring-2 peer-focus-visible:ring-primary-500 ${
                force ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform duration-150 ${force ? 'translate-x-4' : 'translate-x-0.5'}`}
              />
            </span>
            <span>
              <span className="block font-medium text-gray-900 dark:text-gray-100">Also send to people who already hold a current completion</span>
              <span className={metaCls}>
                Off: anyone with a current completion or a standing waiver is left alone. On: they get a new assignment too, and a
                waiver is lifted.
              </span>
            </span>
          </label>
          {preview && (
            <p className={metaCls} aria-live="polite" data-testid="details-tally">
              {`${peopleCount(included.length)}${tallyLine ? `: ${tallyLine}` : ''}`}
            </p>
          )}
        </section>
      )}

      {step === 3 && course && (
        <section className={`${cardCls} space-y-4`} aria-labelledby="lms-assign-review">
          <h2 id="lms-assign-review" className={sectionTitleCls}>
            Review and send
          </h2>
          <dl className="grid gap-4 sm:grid-cols-2">
            <Fact label="Course" testId="review-course">
              {course.title}
            </Fact>
            <Fact label="Version" testId="review-version">
              {pinVersion && course.current_version
                ? `Pinned to version ${course.current_version.version_no}`
                : 'The version current when each person starts'}
            </Fact>
            <Fact label="Sent as" testId="review-kind">
              {CAMPAIGN_KIND_LABELS[kind]}
            </Fact>
            <Fact label="Due" testId="review-due">
              {days !== null ? `Within ${days} ${days === 1 ? 'day' : 'days'} (${dueOn})` : '\u2014'}
            </Fact>
            <Fact label="Audience" testId="review-audience">
              {describeAudience(audience, siteNames).join('; ')}
            </Fact>
            <Fact label="People" testId="review-people">
              {`${peopleCount(included.length)}${excluded.length > 0 ? `, ${excluded.filter((id) => preview?.recipients.some((r) => r.user_id === id)).length} left out` : ''}`}
              {tallyLine && <span className={`${metaCls} block`}>{tallyLine}</span>}
            </Fact>
            <Fact label="Also send to current holders" testId="review-force">
              {force ? 'Yes' : 'No'}
            </Fact>
            <Fact label="Message" testId="review-message">
              {message.trim() ? <span className="whitespace-pre-line">{message.trim()}</span> : <span className={metaCls}>None</span>}
            </Fact>
          </dl>
          {sendError && <ErrorNote testId="send-error">{sendError}</ErrorNote>}
          {previewError && <ErrorNote>{previewError}</ErrorNote>}
        </section>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <button type="button" className={secondaryBtnCls} onClick={back} disabled={step === 0} data-testid="wizard-back">
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
        {step < 3 ? (
          <button type="button" className={primaryBtnCls} onClick={next} disabled={!canLeave[step]} data-testid="wizard-next">
            Next
            <ArrowRight className="h-4 w-4" />
          </button>
        ) : (
          <button type="button" className={primaryBtnCls} onClick={send} disabled={!canSend} data-testid="send-campaign">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {`Send to ${peopleCount(included.length)}`}
          </button>
        )}
      </div>
      {step === 1 && !audienceIsEmpty(audience) && preview && included.length === 0 && !previewLoading && (
        <p className={metaCls}>Everyone is left out: tick at least one person to go on.</p>
      )}
    </div>
  );
}

export default AssignView;
